/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { rome as moment } from '../../../../utils/date';
import Modal from '../../../../shared/modal';
import { ACTION_STATUSES } from '../../../../utils/constants/feedback-statuses';
import { IMPORTANCE_LEVELS } from '../../../../utils/constants/importance-rules';
import { useUploadChatFileMutation } from '../../../../store/chatbox/chatboxApi';
import { useAppDispatch } from '../../../../app/hooks';
import {
    tryToFetchSingleTrip,
} from '../../../../store/admin/trips/adminTripsSlice';

/* ── Category / Sub-category options ── */
const CATEGORY_OPTIONS: Array<{
    name: string;
    value: string;
    subcategories?: { name: string; value: string }[];
}> = [
    {
        name: 'Cabin', value: 'Cabin',
        subcategories: [
            { name: 'Bathroom', value: 'bathroom' }, { name: 'Bed', value: 'bed' },
            { name: 'Temperature', value: 'temperature' }, { name: 'Comfort', value: 'comfort' },
        ],
    },
    {
        name: 'Restaurant', value: 'Restaurant',
        subcategories: [
            { name: 'Food & Beverage', value: 'Food & Beverage' }, { name: 'Timings', value: 'Timings' },
            { name: 'Service', value: 'Service' }, { name: 'Temperature', value: 'Temperature' },
            { name: 'Restaurant Staff', value: 'Restaurant Staff' }, { name: 'Menu', value: 'Menu' },
        ],
    },
    {
        name: 'Bar', value: 'Bar',
        subcategories: [
            { name: 'Choice', value: 'Choice' }, { name: 'Comfort', value: 'Comfort' },
            { name: 'Ambience', value: 'Ambience' }, { name: 'Service', value: 'Service' },
        ],
    },
    {
        name: 'Lounge', value: 'Lounge',
        subcategories: [
            { name: 'Comfort', value: 'Comfort' }, { name: 'Ambience', value: 'Ambience' },
            { name: 'Service', value: 'Service' }, { name: 'Welcome', value: 'Welcome' },
            { name: 'Food & Beverage', value: 'Food & Beverage' }, { name: 'Entertainment', value: 'Entertainment' },
        ],
    },
    {
        name: 'Boarding', value: 'Boarding',
        subcategories: [
            { name: 'Convenience', value: 'Convenience' }, { name: 'Comfort', value: 'Comfort' },
            { name: 'Service', value: 'Service' }, { name: 'Welcome', value: 'Welcome' },
        ],
    },
    {
        name: 'Onboard Experience', value: 'Onboard Experience',
        subcategories: [
            { name: 'Crew Bar', value: 'Crew Bar' }, { name: 'Crew Restaurant', value: 'Crew Restaurant' },
            { name: 'Conductor', value: 'Conductor' }, { name: 'Train Manager', value: 'Train Manager' },
            { name: 'Entertainment', value: 'Entertainment' }, { name: 'Special Events', value: 'Special Events' },
            { name: 'Train Design', value: 'Train Design' },
        ],
    },
    {
        name: 'Offboard Experience', value: 'Offboard Experience',
        subcategories: [
            { name: 'Restaurants', value: 'Restaurants' }, { name: 'Experience', value: 'Experience' },
            { name: 'Transfers', value: 'Transfers' }, { name: 'Local Guide', value: 'Local Guide' },
            { name: 'Length', value: 'Length' }, { name: 'Quality', value: 'Quality' },
            { name: 'Service & Guest Support', value: 'Service & Guest Support' },
            { name: 'Value for Money', value: 'Value for Money' }, { name: 'Content', value: 'Content' },
        ],
    },
    {
        name: 'Service', value: 'Service',
        subcategories: [
            { name: 'Guest Communication', value: 'Guest Communication' }, { name: 'Transfer', value: 'Transfer' },
        ],
    },
    { name: 'General Experience', value: 'General Experience', subcategories: [] },
];

const ALL_SUBCATEGORIES: { name: string; value: string }[] = (() => {
    const map = new Map<string, string>();
    CATEGORY_OPTIONS.forEach((c) =>
        (c.subcategories || []).forEach((s) => {
            if (!map.has(s.value)) map.set(s.value, s.name);
        }),
    );
    return Array.from(map.entries()).map(([value, name]) => ({ value, name }));
})();

const SENTIMENT_OPTIONS = [
    { name: 'Positive', value: 'positive', color: '#10B981' },
    { name: 'Neutral', value: 'neutral', color: '#F59E0B' },
    { name: 'Negative', value: 'negative', color: '#EF4444' },
];

const IMPORTANCE_COLOR_MAP: Record<string, string> = {
    High: '#EF4444',
    Medium: '#F59E0B',
    Low: '#3B82F6',
};

const IMPORTANCE_BG_MAP: Record<string, string> = {
    High: '#FEF2F2',
    Medium: '#FFFBEB',
    Low: '#EFF6FF',
};

/* ── Media helpers ── */
type FileItem = { name: string; url: string };

const isImage = (url: string) => {
    if (typeof url !== 'string') return false;
    return /\.(jpg|jpeg|png|gif|webp|avif|bmp|svg)$/i.test(url?.split('?')[0] || '');
};

const isVideo = (url: string) => {
    if (typeof url !== 'string') return false;
    return /\.(mp4|webm|ogg|mov|m4v)$/i.test(url?.split('?')[0] || '');
};

/* ── Styled field wrapper ── */
const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <p className='text-[13px] font-medium text-[#0A0D12] dark:text-white mb-1.5 font-inter'>{children}</p>
);

const FieldBox = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
    <div className={`border border-[#E9EAEB] rounded-[8px] px-3 py-2.5 text-[14px] text-[#0A0D12] dark:text-white font-inter min-h-[42px] flex items-center ${className}`}>
        {children}
    </div>
);

/* ── Chevron style for native selects ── */
const chevronStyle: React.CSSProperties = {
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath d='M2.5 4l2.5 2.5L7.5 4' stroke='%23717680' stroke-width='1.2' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    paddingRight: '28px',
};

/* ================================================================ */
/*  Component                                                        */
/* ================================================================ */
const FeedbackDetailsModal = ({
    open,
    feedback,
    onClose,
    onSave,
    onDelete,
    isSaving,
}: {
    open: boolean;
    feedback: any;
    onClose: () => void;
    onSave: (id: string, payload: Record<string, any>) => Promise<void>;
    onDelete?: (id: string) => Promise<void>;
    isSaving?: boolean;
}) => {
    /* ── Hooks ── */
    const dispatch = useAppDispatch();
    const [uploadFile] = useUploadChatFileMutation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    /* ── Local editable state ── */
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [subCategory, setSubCategory] = useState('');
    const [sentiment, setSentiment] = useState('');
    const [importance, setImportance] = useState('');
    const [action, setAction] = useState(false);
    const [actionStatus, setActionStatus] = useState('');

    /* ── File management state ── */
    const [currentFiles, setCurrentFiles] = useState<FileItem[]>([]);
    const [addedFiles, setAddedFiles] = useState<FileItem[]>([]);
    const [removedFileUrls, setRemovedFileUrls] = useState<string[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    /* ── Delete state ── */
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    /* ── Carriage / Cabin state ── */
    const [tripComposition, setTripComposition] = useState<any[]>([]);
    const [selectedCabinId, setSelectedCabinId] = useState('');
    const [saveError, setSaveError] = useState<string | null>(null);

    /* ── Populate state from feedback ── */
    useEffect(() => {
        if (feedback) {
            setDescription(feedback?.description || feedback?.additionalInfo?.description || '');
            setCategory(feedback?.additionalInfo?.category || '');
            setSubCategory(feedback?.additionalInfo?.subCategory || '');
            setSentiment(feedback?.additionalInfo?.sentiment || '');
            setImportance(feedback?.additionalInfo?.importance || 'Medium');
            setAction(feedback?.action ?? false);
            setActionStatus(feedback?.actionStatus || 'unallocated');

            const files = feedback?.files || feedback?.additionalInfo?.files || [];
            setCurrentFiles(Array.isArray(files) ? files : []);
            setAddedFiles([]);
            setRemovedFileUrls([]);

            /* Set initial cabin value */
            setSelectedCabinId(feedback?.additionalInfo?.cabinId || feedback?.additionalInfo?.cabin || '');
        }
    }, [feedback, open]);

    /* ── Fetch trip composition on open ── */
    useEffect(() => {
        if (!open || !feedback) return;
        const tripId = feedback?.additionalInfo?.tripId || feedback?.trip?._id || feedback?.tripId || '';
        if (tripId) {
            dispatch(tryToFetchSingleTrip(tripId))
                .unwrap()
                .then((data: any) => {
                    const comp = data?.data?.trainComposition || data?.trainComposition || [];
                    setTripComposition(Array.isArray(comp) ? comp : []);
                })
                .catch(() => setTripComposition([]));
        } else {
            setTripComposition([]);
        }
    }, [open, feedback]);

    /* ── Build flat cabin list from trip composition ── */
    type CabinEntry = { cabinId: string; carriageTechnicalName: string; commercialName: string; carriageType: string };
    const allCabins: CabinEntry[] = tripComposition.flatMap((carriage: any) => {
        const letter = carriage.commercialName || '';
        const count = carriage.numberOfCabins || 0;
        return Array.from({ length: count }, (_, i) => ({
            cabinId: `${letter}${i + 1}`,
            carriageTechnicalName: carriage.technicalName || '',
            commercialName: letter,
            carriageType: carriage.type || '',
        }));
    });

    /* Group cabins by carriage for the dropdown */
    const cabinGroups = tripComposition.map((carriage: any) => ({
        technicalName: carriage.technicalName || '',
        commercialName: carriage.commercialName || '',
        type: carriage.type || '',
        cabins: allCabins.filter((c) => c.carriageTechnicalName === carriage.technicalName),
    }));

    /** Derive the displayed technical name from the selected cabin */
    const displayedTechnicalName = (() => {
        if (!selectedCabinId) return feedback?.additionalInfo?.carriageTechnicalName || '';
        const match = allCabins.find((c) => c.cabinId === selectedCabinId);
        return match?.carriageTechnicalName || feedback?.additionalInfo?.carriageTechnicalName || '';
    })();

    /* ── Derived data ── */
    const dateReceived = feedback?.createdAt
        ? moment(feedback.createdAt).format('D MMM YYYY [at] h:mm a')
        : '';
    const tripName = feedback?.additionalInfo?.journeyTitle || feedback?.trip?.itinerary?.journeyTitle || feedback?.additionalInfo?.tripName || '';

    /* Visible files = original files (minus removed) + newly added */
    const visibleFiles: FileItem[] = [
        ...currentFiles.filter((f) => !removedFileUrls.includes(f.url)),
        ...addedFiles,
    ];

    /* ── File handlers ── */
    const onRemoveFile = (url: string) => {
        /* If it's a newly added file, just remove from addedFiles */
        const isNewlyAdded = addedFiles.some((f) => f.url === url);
        if (isNewlyAdded) {
            setAddedFiles((prev) => prev.filter((f) => f.url !== url));
        } else {
            /* It's an original file - track for removal */
            setRemovedFileUrls((prev) => [...prev, url]);
        }
    };

    const onAddFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (!fileList || fileList.length === 0) return;

        setIsUploading(true);
        try {
            const formData = new FormData();
            Array.from(fileList).forEach((file) => formData.append('files', file));
            const uploaded = await uploadFile(formData).unwrap();
            if (Array.isArray(uploaded)) {
                setAddedFiles((prev) => [...prev, ...uploaded]);
            }
        } catch (err) {
            console.error('Failed to upload file(s):', err);
        } finally {
            setIsUploading(false);
            /* Reset input so the same file can be re-selected */
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    /* ── Clear inline error when modal re-opens or feedback changes ── */
    useEffect(() => { setSaveError(null); }, [open, feedback]);

    /* ── Available subcategories based on selected category ── */
    const availableSubcategories = (() => {
        const cat = CATEGORY_OPTIONS.find((c) => c.value === category);
        return cat?.subcategories && cat.subcategories.length > 0 ? cat.subcategories : ALL_SUBCATEGORIES;
    })();

    /* ── When category changes, validate subcategory ── */
    const onChangeCategory = (val: string) => {
        setCategory(val);
        if (val) {
            const cat = CATEGORY_OPTIONS.find((c) => c.value === val);
            const valid = (cat?.subcategories || []).some((s) => s.value === subCategory);
            if (!valid) setSubCategory('');
        } else {
            setSubCategory('');
        }
    };

    /* ── Sentiment display ── */
    const sentimentInfo = SENTIMENT_OPTIONS.find((s) => s.value === sentiment);

    /* ── Action status display ── */
    const actionStatusInfo = ACTION_STATUSES.find((s) => s.value === actionStatus);

    /* ── Delete feedback ── */
    const handleDelete = async () => {
        if (!onDelete || !feedback?._id) return;
        setIsDeleting(true);
        try {
            await onDelete(feedback._id);
            setConfirmDeleteOpen(false);
            onClose();
        } catch (err: any) {
            const msg = typeof err === 'string' ? err : err?.message || 'Failed to delete feedback';
            setSaveError(msg);
        } finally {
            setIsDeleting(false);
        }
    };

    /* ── Build and submit only changed fields ── */
    const handleConfirm = async () => {
        setSaveError(null);
        const payload: Record<string, any> = {};

        const origDesc = feedback?.description || feedback?.additionalInfo?.description || '';
        if (description !== origDesc) payload.description = description;

        const origCat = feedback?.additionalInfo?.category || '';
        if (category !== origCat) payload.category = category || null;

        const origSub = feedback?.additionalInfo?.subCategory || '';
        if (subCategory !== origSub) payload.subCategory = subCategory || null;

        const origSentiment = feedback?.additionalInfo?.sentiment || '';
        if (sentiment !== origSentiment && !payload.description) payload.sentiment = sentiment;

        const origImportance = feedback?.additionalInfo?.importance || 'Medium';
        if (importance !== origImportance) {
            payload.importance = importance;
            payload.originalImportance = origImportance;
        }

        const origAction = feedback?.action ?? false;
        if (action !== origAction) payload.action = action;

        const origActionStatus = feedback?.actionStatus || 'unallocated';
        if (actionStatus !== origActionStatus) payload.actionStatus = actionStatus;

        /* Cabin change — backend auto-resolves carriageTechnicalName + commercialName */
        const origCabinId = feedback?.additionalInfo?.cabinId || feedback?.additionalInfo?.cabin || '';
        if (selectedCabinId && selectedCabinId !== origCabinId) {
            payload.cabinId = selectedCabinId;
            payload.cabin = selectedCabinId;
        }

        /* File changes */
        if (removedFileUrls.length > 0) {
            payload.removeFiles = removedFileUrls;
        }
        if (addedFiles.length > 0) {
            payload.addFiles = addedFiles.map((f) => ({ name: f.name, url: f.url }));
        }

        if (Object.keys(payload).length === 0) {
            onClose();
            return;
        }

        try {
            await onSave(feedback._id, payload);
        } catch (err: any) {
            /* Show the backend error message inline (e.g. commercial name conflict) */
            const msg = typeof err === 'string' ? err : err?.message || 'Failed to save changes';
            setSaveError(msg);
        }
    };

    /* ── Cabin display (fallback when no trip composition data) ── */
    const cabinDisplay = selectedCabinId || feedback?.additionalInfo?.cabinId || feedback?.additionalInfo?.cabin || 'N/A';

    return (
        <Modal
            open={open}
            title='Feedback Details'
            onClose={onClose}
            titleStyle='!text-[18px] !font-semibold !text-[#0A0D12]'
            contentContainerStyle='!rounded-2xl !max-w-[560px] !w-[560px] !p-5'
        >
            <div className='flex flex-col gap-4 mt-2'>
                {/* ── Row: Verbatim | Media ── */}
                <div className='grid grid-cols-2 gap-4'>
                    <div>
                        <FieldLabel>Verbatim</FieldLabel>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            className='w-full border border-[#E9EAEB] rounded-[8px] px-3 py-2.5 text-[14px] text-[#0A0D12] font-inter resize-none focus:outline-none focus:border-[#2D8EFF] dark:bg-[#111317] dark:text-white'
                        />
                    </div>
                    <div>
                        <FieldLabel>Media</FieldLabel>
                        <div className='border border-[#E9EAEB] rounded-[8px] px-2 py-2 min-h-[110px]'>
                            {visibleFiles.length === 0 && !isUploading && (
                                <p className='text-[13px] text-[#A4A7AE] font-inter pt-1 px-1'>Empty</p>
                            )}

                            {/* File thumbnails */}
                            {visibleFiles.length > 0 && (
                                <div className='flex flex-wrap gap-2 mb-2'>
                                    {visibleFiles.map((file, idx) => (
                                        <div key={file.url + idx} className='relative group w-[56px] h-[56px] rounded-[6px] overflow-hidden border border-[#E9EAEB] bg-[#FAFAFA] flex-shrink-0'>
                                            {isImage(file.url) ? (
                                                <img
                                                    src={file.url}
                                                    alt={file.name}
                                                    className='w-full h-full object-cover'
                                                />
                                            ) : isVideo(file.url) ? (
                                                <div className='w-full h-full flex items-center justify-center bg-black/5'>
                                                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                                                        <path d='M8 5v14l11-7L8 5z' fill='#717680' />
                                                    </svg>
                                                </div>
                                            ) : (
                                                <div className='w-full h-full flex items-center justify-center'>
                                                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                                                        <path d='M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z' stroke='#717680' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                                                        <path d='M14 2v6h6' stroke='#717680' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                                                    </svg>
                                                </div>
                                            )}

                                            {/* Delete overlay */}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onRemoveFile(file.url); }}
                                                className='absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center'
                                                title={`Remove ${file.name}`}
                                            >
                                                <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                                                    <path d='M3.5 3.5l7 7M10.5 3.5l-7 7' stroke='white' strokeWidth='1.6' strokeLinecap='round' />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Uploading indicator */}
                            {isUploading && (
                                <div className='flex items-center gap-2 px-1 py-1'>
                                    <svg className='h-4 w-4 animate-spin text-gray-400' viewBox='0 0 24 24' fill='none'>
                                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4A4 4 0 008 12H4z' />
                                    </svg>
                                    <span className='text-[12px] text-[#717680] font-inter'>Uploading...</span>
                                </div>
                            )}

                            {/* Add file button */}
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploading || isSaving}
                                className='flex items-center gap-1.5 px-2 py-1.5 rounded-[6px] text-[12px] font-inter text-[#2D8EFF] hover:bg-[#2D8EFF0D] transition disabled:opacity-40'
                            >
                                <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                                    <path d='M7 2.5v9M2.5 7h9' stroke='#2D8EFF' strokeWidth='1.6' strokeLinecap='round' />
                                </svg>
                                Add file
                            </button>
                            <input
                                ref={fileInputRef}
                                type='file'
                                accept='image/*,video/*'
                                multiple
                                className='hidden'
                                onChange={onAddFiles}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Row: Date Received | Trip ── */}
                <div className='grid grid-cols-2 gap-4'>
                    <div>
                        <FieldLabel>Date Received</FieldLabel>
                        <FieldBox>{dateReceived || 'N/A'}</FieldBox>
                    </div>
                    <div>
                        <FieldLabel>Trip</FieldLabel>
                        <FieldBox>{tripName || 'N/A'}</FieldBox>
                    </div>
                </div>

                {/* ── Row: Carriage Technical Name (read-only) | Cabin (dropdown) ── */}
                <div className='grid grid-cols-2 gap-4'>
                    <div>
                        <FieldLabel>Carriage Technical Name</FieldLabel>
                        <FieldBox>{displayedTechnicalName || 'N/A'}</FieldBox>
                    </div>
                    <div>
                        <FieldLabel>Cabin</FieldLabel>
                        {allCabins.length > 0 ? (
                            <select
                                value={selectedCabinId}
                                onChange={(e) => setSelectedCabinId(e.target.value)}
                                className='w-full border border-[#E9EAEB] rounded-[8px] px-3 py-2.5 text-[14px] text-[#0A0D12] font-inter bg-white appearance-none cursor-pointer focus:outline-none focus:border-[#2D8EFF]'
                                style={chevronStyle}
                            >
                                <option value=''>Select cabin</option>
                                {cabinGroups.map((group) => (
                                    <optgroup key={group.technicalName} label={`${group.technicalName} — ${group.type}`}>
                                        {group.cabins.map((c) => (
                                            <option key={c.cabinId} value={c.cabinId}>{c.cabinId}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        ) : (
                            <FieldBox>{cabinDisplay}</FieldBox>
                        )}
                    </div>
                </div>

                {/* ── Row: Category | Subcategory ── */}
                <div className='grid grid-cols-2 gap-4'>
                    <div>
                        <FieldLabel>Category</FieldLabel>
                        <select
                            value={category}
                            onChange={(e) => onChangeCategory(e.target.value)}
                            className='w-full border border-[#E9EAEB] rounded-[8px] px-3 py-2.5 text-[14px] text-[#0A0D12] dark:bg-[#111317] dark:text-white font-inter bg-white appearance-none cursor-pointer focus:outline-none focus:border-[#2D8EFF]'
                            style={chevronStyle}
                        >
                            <option value=''>Select category</option>
                            {CATEGORY_OPTIONS.map((c) => (
                                <option key={c.value} value={c.value}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <FieldLabel>Subcategory</FieldLabel>
                        <select
                            value={subCategory}
                            onChange={(e) => setSubCategory(e.target.value)}
                            className='w-full border border-[#E9EAEB] rounded-[8px] px-3 py-2.5 text-[14px] text-[#0A0D12] dark:bg-[#111317] dark:text-white font-inter bg-white appearance-none cursor-pointer focus:outline-none focus:border-[#2D8EFF]'
                            style={chevronStyle}
                        >
                            <option value=''>Select subcategory</option>
                            {availableSubcategories.map((s) => (
                                <option key={s.value} value={s.value}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* ── Row: Allocated to | Sentiment ── */}
                <div className='grid grid-cols-2 gap-4'>
                    <div>
                        <FieldLabel>Allocated to</FieldLabel>
                        <FieldBox>
                            {feedback?.personResponsibleForAction?.name || 'N/A'}
                        </FieldBox>
                    </div>
                    <div>
                        <FieldLabel>Sentiment</FieldLabel>
                        <FieldBox>
                            <span style={{ color: sentimentInfo?.color || '#717680' }}>
                                {sentimentInfo?.name || 'N/A'}
                            </span>
                        </FieldBox>
                    </div>
                </div>

                {/* ── Row: Action | Action Status ── */}
                <div className='grid grid-cols-2 gap-4'>
                    <div>
                        <FieldLabel>Action</FieldLabel>
                        <FieldBox>{action ? 'Yes' : 'No'}</FieldBox>
                    </div>
                    <div>
                        <FieldLabel>Action Status</FieldLabel>
                        <div className='relative flex items-center'>
                            <span
                                className='absolute left-3 inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 pointer-events-none z-10'
                                style={{ backgroundColor: actionStatusInfo?.value === 'allocated_and_acknowledged' ? '#10B981' : actionStatusInfo?.value === 'allocated_but_not_acknowledged' ? '#F59E0B' : '#EF4444' }}
                            />
                            <select
                                value={actionStatus}
                                onChange={(e) => setActionStatus(e.target.value)}
                                className='w-full border border-[#E9EAEB] rounded-[8px] pl-8 pr-7 py-2.5 text-[13px] font-inter bg-white dark:bg-[#111317] dark:text-white appearance-none cursor-pointer focus:outline-none focus:border-[#2D8EFF]'
                                style={{
                                    ...chevronStyle,
                                    color: actionStatusInfo?.value === 'allocated_and_acknowledged' ? '#10B981' : actionStatusInfo?.value === 'allocated_but_not_acknowledged' ? '#F59E0B' : '#EF4444',
                                }}
                            >
                                {ACTION_STATUSES.map((s) => (
                                    <option key={s.value} value={s.value}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* ── Row: Importance (full width select) ── */}
                <div>
                    <FieldLabel>Importance</FieldLabel>
                    <select
                        value={importance}
                        onChange={(e) => setImportance(e.target.value)}
                        className='w-full border border-[#E9EAEB] rounded-[8px] px-3 py-2.5 text-[14px] font-inter bg-white dark:bg-[#111317] dark:text-white appearance-none cursor-pointer focus:outline-none focus:border-[#2D8EFF]'
                        style={{
                            ...chevronStyle,
                            color: IMPORTANCE_COLOR_MAP[importance] || '#0A0D12',
                            backgroundColor: IMPORTANCE_BG_MAP[importance] || '#ffffff',
                        }}
                    >
                        {IMPORTANCE_LEVELS.map((level) => (
                            <option key={level} value={level}>{level}</option>
                        ))}
                    </select>
                </div>

                {/* ── Inline save error ── */}
                {saveError && (
                    <div className='bg-red-50 border border-red-200 rounded-[8px] px-3 py-2.5 flex items-start gap-2'>
                        <svg className='w-4 h-4 text-red-500 mt-0.5 flex-shrink-0' viewBox='0 0 20 20' fill='currentColor'>
                            <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z' clipRule='evenodd' />
                        </svg>
                        <p className='text-[13px] text-red-600 font-inter'>{saveError}</p>
                    </div>
                )}

                {/* ── Delete confirmation ── */}
                {confirmDeleteOpen && (
                    <div className='bg-red-50 border border-red-200 rounded-[8px] px-4 py-3 flex flex-col gap-2'>
                        <p className='text-[13px] text-red-700 font-inter font-medium'>
                            Are you sure you want to delete this feedback? This action cannot be undone.
                        </p>
                        <div className='flex gap-2'>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className='px-4 py-1.5 text-[12px] font-inter font-medium rounded-md bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50'
                            >
                                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                            </button>
                            <button
                                onClick={() => setConfirmDeleteOpen(false)}
                                disabled={isDeleting}
                                className='px-4 py-1.5 text-[12px] font-inter font-medium rounded-md bg-white text-[#0A0D12] border border-[#E4E7EC] hover:bg-gray-50 transition disabled:opacity-50'
                            >
                                No, Keep It
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Footer buttons ── */}
                <div className='flex items-center gap-3 mt-2'>
                    {onDelete && (
                        <button
                            onClick={() => setConfirmDeleteOpen(true)}
                            disabled={isSaving || isDeleting}
                            className='h-[44px] px-4 rounded-[8px] border border-red-300 bg-white text-red-600 text-[14px] font-medium font-inter hover:bg-red-50 transition disabled:opacity-50'
                        >
                            Delete
                        </button>
                    )}
                    <div className='flex-1' />
                    <button
                        onClick={onClose}
                        disabled={isSaving || isDeleting}
                        className='h-[44px] px-6 rounded-[8px] border border-[#E9EAEB] bg-white text-[#0A0D12] text-[14px] font-medium font-inter hover:bg-gray-50 transition disabled:opacity-50'
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isSaving || isDeleting}
                        className='h-[44px] px-6 rounded-[8px] bg-[#0A0D12] dark:bg-[#00691f] text-white text-[14px] font-medium font-inter hover:opacity-90 transition disabled:opacity-50'
                    >
                        {isSaving ? 'Saving...' : 'Confirm Changes'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default FeedbackDetailsModal;
